import { useState, useCallback, useRef, useEffect } from 'react'
import { X, Square, Link as LinkIcon } from 'lucide-react'
import type { Workflow, Activity, Transition } from './types'

interface SearchResult {
  type: 'activity' | 'transition'
  item: Activity | Transition
  matches: string[]
}

interface Props {
  open: boolean
  onClose: () => void
  workflow: Workflow
  onSelect: (type: 'activity' | 'transition', id: string) => void
}

export default function SearchDialog({ open, onClose, workflow, onSelect }: Props) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  const results = useCallback((): SearchResult[] => {
    if (!query) return []
    const q = query.toLowerCase()
    const results: SearchResult[] = []

    for (const a of workflow.activities) {
      const matches: string[] = []
      if (a.id.toLowerCase().includes(q)) matches.push(`ID: ${a.id}`)
      if (a.description.toLowerCase().includes(q)) matches.push(`Description: ${a.description}`)
      if (a.performer?.toLowerCase().includes(q)) matches.push(`Performer: ${a.performer}`)
      if (a.tool?.name?.toLowerCase().includes(q)) matches.push(`Tool: ${a.tool.name}`)
      if (a.tool?.parameters?.some(p =>
        p.id.toLowerCase().includes(q) || p.value.toLowerCase().includes(q)
      )) matches.push('Parameter match')
      if (matches.length > 0) results.push({ type: 'activity', item: a, matches })
    }

    for (const t of workflow.transitions) {
      const matches: string[] = []
      if (t.id.toLowerCase().includes(q)) matches.push(`ID: ${t.id}`)
      if (t.description?.toLowerCase().includes(q)) matches.push(`Description: ${t.description}`)
      if (t.condition?.toLowerCase().includes(q)) matches.push(`Condition: ${t.condition}`)
      if (matches.length > 0) results.push({ type: 'transition', item: t, matches })
    }

    return results
  }, [query, workflow])()

  if (!open) return null

  return (
    <div style={{
      position: 'absolute',
      top: 16,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 360,
      maxHeight: 400,
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 8,
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
      zIndex: 200,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Search input */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }}>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search components..."
          onKeyDown={e => e.key === 'Escape' && onClose()}
          style={{
            flex: 1, background: 'transparent', border: 'none',
            color: 'var(--color-text)', fontSize: '0.9125rem', outline: 'none',
          }}
        />
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--color-text-secondary)', padding: 4,
        }}>
          <X size={16} />
        </button>
      </div>

      {/* Results */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {query && results.length === 0 && (
          <div style={{ padding: 16, textAlign: 'center', color: 'var(--color-text-secondary)', fontSize: '0.8625rem' }}>
            No matches found
          </div>
        )}
        {results.map(r => (
          <button
            key={`${r.type}-${r.item.id}`}
            onClick={() => {
              onSelect(r.type, r.item.id)
              onClose()
            }}
            style={{
              display: 'block',
              width: '100%',
              padding: '8px 12px',
              textAlign: 'left',
              background: 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--color-border)',
              cursor: 'pointer',
              color: 'var(--color-text)',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--color-bg)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8625rem', fontWeight: 500 }}>
              {r.type === 'activity' ? <Square size={12} /> : <LinkIcon size={12} />}
              {(r.item as Activity).description || r.item.id}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
              {r.matches.join(' | ')}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
